import { Alert, Box, CircularProgress, Container } from '@mui/material';
import { useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import RoleTemplateReplayPresentation from '../components/template/RoleTemplateReplayPresentation';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { Securables } from '../Enums';
import RoleTemplateReplayService, {
    RoleTemplateReplay,
    isRoleTemplateReplayOrganizationAllowed,
} from '../services/RoleTemplateReplayService';
import { NORTHSTAR_ROLE_TEMPLATE_EXPECTED_SERIALIZATION } from '../services/RoleTemplateReplayFingerprint';

export const serializeRoleTemplateReplayCaptureContract = (replay: RoleTemplateReplay) => JSON.stringify({
    Version: replay.Version,
    IsSynthetic: replay.IsSynthetic,
    ReadOnly: replay.ReadOnly,
    OrganizationName: replay.OrganizationName,
    PositionTitle: replay.PositionTitle,
    Activities: replay.Activities,
    Talent: replay.Talent,
    TemplateStatus: replay.TemplateStatus,
    Template: {
        Version: replay.Template.Version,
        IsSynthetic: replay.Template.IsSynthetic,
        ReadOnly: replay.Template.ReadOnly,
        OrganizationName: replay.Template.OrganizationName,
        ScenarioName: replay.Template.ScenarioName,
        TemplateName: replay.Template.TemplateName,
        ProficiencyGroups: replay.Template.ProficiencyGroups,
        Criteria: replay.Template.Criteria,
        AbilityLegend: replay.Template.AbilityLegend,
        BehaviorLegend: replay.Template.BehaviorLegend,
    },
});

export const isCaptureSafeRoleTemplateReplay = (replay: RoleTemplateReplay, selectedOrganizationId: string) =>
    replay.OrganizationId === selectedOrganizationId
    && replay.Template.OrganizationId === selectedOrganizationId
    && serializeRoleTemplateReplayCaptureContract(replay) === NORTHSTAR_ROLE_TEMPLATE_EXPECTED_SERIALIZATION;

export const RoleTemplateReplayRoute = () => {
    const { accessInfo, hasOneOfRoles } = useAccessInfo();
    const authorizedRole = hasOneOfRoles([Securables.organization_administrator, Securables.synergist_admin], true);
    const selectedNorthstar = isRoleTemplateReplayOrganizationAllowed(
        accessInfo?.selectedOrganizationId ?? '',
        accessInfo?.selectedOrganizationName ?? '',
    );
    if (!authorizedRole || !selectedNorthstar)
        return <Redirect to="/" />;
    return <RoleTemplateReplayPage />;
};

const RoleTemplateReplayPage = () => {
    const history = useHistory();
    const { accessInfo } = useAccessInfo();
    const selectedOrganizationId = accessInfo?.selectedOrganizationId ?? '';
    const selectedOrganizationName = accessInfo?.selectedOrganizationName ?? '';
    const [replay, setReplay] = useState<RoleTemplateReplay | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        setReplay(null);
        setFailed(false);
        new RoleTemplateReplayService().Get(selectedOrganizationId, selectedOrganizationName, controller.signal)
            .then(result => {
                if (!controller.signal.aborted && isCaptureSafeRoleTemplateReplay(result, selectedOrganizationId))
                    setReplay(result);
                else if (!controller.signal.aborted)
                    setFailed(true);
            })
            .catch(() => { if (!controller.signal.aborted) setFailed(true); });
        return () => controller.abort();
    }, [selectedOrganizationId, selectedOrganizationName]);

    if (failed)
        return <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="warning">Synthetic role-to-template replay is unavailable for this organization.</Alert></Container>;
    if (!replay)
        return <Box role="status" aria-label="Loading synthetic role-to-template replay" sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

    return <RoleTemplateReplayPresentation replay={replay} onClose={() => history.push('/PositionManagement')} />;
};

export default RoleTemplateReplayPage;
